/* eslint react/prop-types: 0 */
import React, { FC } from 'react';

import { compose } from 'react-recompose';
import { withTranslation } from 'react-i18next';
import { withStyles, createStyles } from '@material-ui/core/styles';

import dayjs from 'dayjs';
import moment from 'moment';
import { values, flatten, find, forEach, reduce, isArray, noop } from 'lodash-es';

import {
    ElasticTheme,
    SimplePopover,
    NumberInput,
    BasicInput,
    SingleDatePicker,
    DotsIcon,
    SimplePopoverOptionsType,
} from '@dev/ee-components';

import { RuleValueOptions, ConditionOptions, RuleFieldOptions } from './selectOptions';

import {
    RuleConditionType,
    RuleConditionTypeToConditionOptions,
    SegmentRuleRowProps,
    ReturnEventHandler,
    OptionInterface,
    SegmentRuleRowState,
    RuleFieldOptionInterface,
} from './SegmentRuleToolInterface';
import { MonthDaySelector } from './components/MonthDaySelector';

const styles = createStyles({
    rowRoot: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    ruleFieldSelect: {
        flexGrow: 1,
        margin: '5px 24px 5px 0',
    },
    ruleConditionSelect: {
        width: '137px',
        marginRight: '24px',
    },
    ruleValueInput: {
        minWidth: '417px',
        flexGrow: 1,
        marginRight: '24px',

        [ElasticTheme.breakpoints.down('xs')]: {
            minWidth: '100px',
        },
    },
    dotsIconWrapper: {
        cursor: 'pointer',
    },
    pickersWrapper: {
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
    },
    twoLastPickersWrapper: {
        display: 'flex',
        flexGrow: 1,
        margin: '5px 0',
    },
    ellipsisWrapper: {
        margin: '5px 0',
    },
});

/**
 * Handler for any change in the 1st row select
 * @param props - SegmentRuleRow Props
 */
const handleChangeRuleFieldSelect =
    (props: SegmentRuleRowProps): ReturnEventHandler =>
    (event, pickedField): void => {
        const conditionalApostrophe =
            pickedField?.ruleValueType === 'Number' || pickedField?.ruleValueType === 'Boolean' ? '' : "'";
        const conditionalValue = pickedField.ruleValueType === 'Number' ? 0 : '';

        props.onChangeRuleFieldSelect(
            `${pickedField?.value || ''} = ${conditionalApostrophe}${conditionalValue}${conditionalApostrophe}`
        );
    };

/**
 * Prints the 1st select in the rule row (Rule Field Select)
 * @param ruleField - Custom field or predefined rule field object (contains row rendering info
 *                      on which condition options and value options should be displayed in current row)
 * @param props - SegmentRuleRow props
 */
const PrintRuleFieldSelect = (ruleField: RuleFieldOptionInterface, props: SegmentRuleRowProps): JSX.Element => (
    <div className={props.classes.ruleFieldSelect}>
        <SimplePopover
            id={`rule-field-row-${props.index}`}
            popperProps={{ disablePortal: false }}
            type="input"
            enableCheckbox={false}
            maxHeight={240}
            visibleScrollbar={true}
            fullWidth={true}
            defaultPlaceholder={props.t('rulefield')}
            value={ruleField?.value}
            options={RuleFieldOptions as unknown as SimplePopoverOptionsType}
            onItemClick={handleChangeRuleFieldSelect(props)}
            error={ruleField?.isNotFieldFound}
        />
    </div>
);

/**
 * Prints 2nd select in rule row (Rule Condition Select)
 * @param ruleConditionType - determines which set of options to use for the printed select element
 * @param value - currently picked condition
 * @param onChange - change handler for condition select
 * @param props - SegmentRuleRow props
 */
const PrintRuleConditionSelect = (
    ruleConditionType: RuleConditionType,
    value: string,
    onChange: ReturnEventHandler,
    props: SegmentRuleRowProps
): JSX.Element => (
    <div className={props.classes.ruleConditionSelect}>
        <SimplePopover
            id={`rule-condition-row-${props.index}`}
            popperProps={{ disablePortal: false }}
            type="input"
            enableCheckbox={false}
            maxHeight={240}
            visibleScrollbar={true}
            fullWidth={true}
            defaultPlaceholder={props.t('rulecondition')}
            value={value}
            options={ConditionOptions[RuleConditionTypeToConditionOptions[ruleConditionType]]}
            onItemClick={onChange}
        />
    </div>
);

/**
 * This function CAN NOT be used before contact lists were appended to RuleValueOptions from './selectOptions'
 * Prints 3rd select in rule row (Rule Value Select)
 * @param ruleField - currently picked ruleField object (item picked in 1st select of the row)
 * @param pickedCondition - currently picked condition object (item picked in 2nd select of the rule row)
 * @param ruleValue - currently picked rule value (item picked in 3rd select of the rule - I.e. this function's returned select)
 * @param onChange - change handler that can handle change of any from returned elements
 * @param props - SegmentRuleRow props
 */
const PrintRuleValueInput = (
    ruleField: RuleFieldOptionInterface,
    pickedCondition: OptionInterface,
    ruleValue: string | number,
    onChange: ReturnEventHandler,
    props: SegmentRuleRowProps
): JSX.Element => {
    const { ruleValueType, ruleConditionType } = ruleField;
    let isMonthDayCondition;
    let date: moment.Moment;

    switch (ruleValueType) {
        case 'Number':
            return (
                <div className={props.classes.ruleValueInput}>
                    <NumberInput
                        id={`rule-value-number-input-row-${props.index}`}
                        value={ruleValue as number}
                        name="numberInput-name"
                        onChange={onChange}
                        onClickButton={onChange}
                        step={ruleConditionType === 'currency' ? 0.1 : 1}
                    />
                </div>
            );
        case 'Date':
        case 'Datetime': {
            ruleValue = String(ruleValue).replace(/\//g, '-');
            ruleValue =
                String(ruleValue).match(/-/g) && String(ruleValue).match(/-/g).length === 1
                    ? `${moment().year()}-${ruleValue}`
                    : ruleValue;
            isMonthDayCondition = pickedCondition?.value === 'month-day';

            date = ruleValue ? moment(ruleValue) : null;

            if (isMonthDayCondition) {
                return (
                    <MonthDaySelector date={date} ruleValue={ruleValue} onChange={onChange} classes={props.classes} />
                );
            }

            return (
                <div className={props.classes.ruleValueInput}>
                    <SingleDatePicker
                        id={`rule-single-datepicker-row-${props.index}`}
                        date={date}
                        displayFormat={isMonthDayCondition ? 'MM-DD' : 'YYYY-MM-DD'}
                        inputJustifyContent="center"
                        inputMinWidth={120}
                        fullWidth={true}
                        onChange={onChange}
                        returnRawData={true}
                    />
                </div>
            );
        }
        case 'Listname':
        case 'Boolean':
        case 'ConsentTracking':
        case 'UnsubscribeReason':
        case 'Source':
        case 'Status':
            return (
                <div className={props.classes.ruleValueInput}>
                    <SimplePopover
                        id={`rule-value-row-${props.index}`}
                        popperProps={{ disablePortal: false }}
                        type="input"
                        options={RuleValueOptions[ruleValueType] as SimplePopoverOptionsType}
                        enableCheckbox={false}
                        visibleScrollbar={false}
                        fullWidth={true}
                        defaultPlaceholder={props.t('pickrulevalue')}
                        value={ruleValue}
                        onItemClick={onChange}
                    />
                </div>
            );
        case 'String':
        default:
            return (
                <div className={props.classes.ruleValueInput}>
                    <BasicInput
                        id={`rule-value-input-row-${props.index}`}
                        name="defaultname"
                        placeholder={`<${props.t('blank')}>`}
                        type="text"
                        value={ruleValue || ''}
                        onChange={onChange}
                    />
                </div>
            );
    }
};

/**
 * Change Handler for 2nd select in rule row (rule condition select)
 * @param props - SegmentRuleRow props
 * @param state - SegmentRuleRow state
 */
const handleChangeRuleConditionSelect =
    (props: SegmentRuleRowProps, state: SegmentRuleRowState): ReturnEventHandler =>
    (event: any, pickedField: OptionInterface): void => {
        let { ruleValue } = state;
        const { ruleField } = state;

        const conditionalApostrophe =
            ruleField?.ruleValueType === 'Number' || ruleField?.ruleValueType === 'Boolean' ? '' : "'";

        // Remove year from date if condition changed to 'month-day'
        if (ruleField.ruleValueType === 'Datetime' && ruleValue) {
            ruleValue = ruleValue.split('-');
            if (pickedField.value === 'month-day') {
                // Remove year from date
                ruleValue = ruleValue.join('-');
                // ruleValue = `${ruleValue[ruleValue.length - 2]}-${ruleValue[ruleValue.length - 1]}`;
            } else {
                // Add year to date if it's not there
                if (ruleValue.length < 3) {
                    ruleValue = `${moment().year()}-${ruleValue[ruleValue.length - 2]}-${
                        ruleValue[ruleValue.length - 1]
                    }`;
                } else {
                    ruleValue = ruleValue.join('-');
                }
            }
        }

        props.onChangeRuleFieldSelect(
            `${ruleField?.value || ''} ${
                pickedField.value
            } ${conditionalApostrophe}${ruleValue}${conditionalApostrophe}`
        );
    };

/**
 * Change Handler for 3rd select in rule row (rule value select)
 * @param props - SegmentRuleRow props
 * @param state - SegmentRuleRow state
 */
const handleChangeRuleValueInput =
    (props: SegmentRuleRowProps, state: any): ReturnEventHandler =>
    /**
     * @param eventOrDate - if passed to datepicker - dateObject | else - useless event
     * @param pickedField - picked value (custom object/number/string - depending on component)
     */
    (eventOrDate: any, pickedField: any): void => {
        let ruleValue;
        const { ruleField, ruleCondition } = state;
        const pickedCondition = ruleCondition?.value || '=';
        const conditionalApostrophe =
            ruleField?.ruleValueType === 'Number' || ruleField?.ruleValueType === 'Boolean' ? '' : "'";

        switch (state.ruleField.ruleValueType) {
            case 'Date':
            case 'Datetime':
                ruleValue = dayjs(eventOrDate).format(
                    'YYYY-MM-DD'
                    // state.ruleCondition?.value === 'month-day' ? 'MM-DD' : 'YYYY-MM-DD'
                );
                break;
            case 'Listname':
            case 'Boolean':
            case 'ConsentTracking':
            case 'UnsubscribeReason':
            case 'Source':
            case 'Status':
                ruleValue = pickedField.value;
                break;
            case 'Number':
                ruleValue = +eventOrDate.target.value || 0;
                break;
            case 'String':
            default:
                ruleValue = eventOrDate?.target?.value;
                break;
        }

        props.onChangeRuleFieldSelect(
            ruleField
                ? `${
                      ruleField?.value || ''
                  } ${pickedCondition} ${conditionalApostrophe}${ruleValue}${conditionalApostrophe}`
                : ''
        );
    };
/**
 * Split single subrule to ruleField, ruleCondition & ruleValue
 * @param rule - single rule from Segment rule string
 */
const splitRule = (rule: string): Record<string, any> => {
    const ruleFieldValue = rule.split(' ')[0];
    const ruleConditionValue = rule.split(' ')[1];
    const ruleValue = rule.substring(rule.split(' ', 2).join().length + 1, rule.length).replace(/'/g, '');
    return { ruleFieldValue, ruleConditionValue, ruleValue };
};

/**
 * Parse single rule to generate object containing current row's state (ruleField, ruleCondition & ruleValue)
 * @param rule - single rule from Segment rule string
 */
export const parseSingleRule = (rule: string): SegmentRuleRowState => {
    // Check if rule is empty
    if (!rule?.length) {
        return {
            // default rule row state
            ruleField: RuleFieldOptions[0] as any,
            ruleCondition: { label: 'is', value: '=' },
            ruleValue: '',
        };
    } else {
        // If rule not empty
        // split rule to ruleField, ruleCondition, ruleValue
        const splitRules = splitRule(rule);
        let ruleField: any = find(
            flatten(values(RuleFieldOptions)),
            (ruleFieldOption: RuleFieldOptionInterface): boolean =>
                ruleFieldOption.value === splitRules.ruleFieldValue.toLowerCase()
        );
        const ruleCondition = find(
            flatten(values(ConditionOptions)),
            (ruleFieldOption: RuleFieldOptionInterface): boolean =>
                ruleFieldOption.value === splitRules.ruleConditionValue
        );
        let ruleValue = splitRules?.ruleValue;
        ruleField = ruleField
            ? ruleField
            : {
                  label: '',
                  value: '',
                  ruleConditionType: 'text',
                  ruleValueType: 'String',
                  isNotFieldFound: true,
              };
        if (ruleField?.ruleValueType === 'Datetime') {
            ruleValue = ruleValue.split(' ')[0];
        }

        return {
            ruleField,
            ruleCondition,
            ruleValue,
        };
    }
};

/**
 * Change parsed Segment rule back to string
 * @param arrayRule - Dynamic array of single rules
 */
export const arrayRuleToString = (arrayRule: (string | string[])[]): string =>
    reduce(
        arrayRule,
        (sum, ruleElement, index): string => {
            if (typeof ruleElement === 'string') {
                return `${sum}${index ? ' ' : ''}${ruleElement}${index < arrayRule.length - 1 ? ' ' : ''}`;
            } else {
                return `${sum} ( ${arrayRuleToString(ruleElement)} )`;
            }
        },
        ''
    );

/**
 * Checks if passed string is something different than a single rule
 * @param text - string to check
 */
function isSpecial(text: string): boolean {
    return text === 'AND' || text === 'OR' || text === ')' || text === '(';
}

/**
 * Prepares words inside rule, adding quotes and spaces if needed
 * @param word - actual word analysed
 * @param index - index of analysed element
 * @param ruleWords - all words in rule
 */
function prepareWordInRule(word: string, index: number, ruleWords: string[]): string {
    return `${index === 0 ? '"' : ''}${isSpecial(ruleWords[index - 1]) ? '"' : index !== 0 ? ' ' : ''}${word}${
        isSpecial(ruleWords[index + 1]) || index === ruleWords.length - 1 ? '"' : ''
    }`;
}

/**
 * Parse whole Segment string rule to generate array of single rules out of Segment rule string
 * @param rule - single rule string
 */
export const parseStringRule = (rule: string): (string | string[])[] => {
    if (!rule) {
        return [];
    }
    const ruleWords = rule.split(' ');
    let parsedRule;
    let JSONRuleString = '[';

    forEach(ruleWords, (word: string, index: number): void => {
        switch (word) {
            case '(':
                JSONRuleString += '[';
                break;
            case ')':
                JSONRuleString += ']';
                break;
            case 'AND':
            case 'OR':
                JSONRuleString += `,"${word}",`;
                break;
            default:
                JSONRuleString += prepareWordInRule(word, index, ruleWords);
                break;
        }
    });
    JSONRuleString += ']';

    parsedRule = JSON.parse(JSONRuleString);
    parsedRule = parsedRule.length === 1 && isArray(parsedRule[0]) ? parsedRule[0] : parsedRule;

    return parsedRule;
};

const SegmentRuleRow: FC<SegmentRuleRowProps> = (props: SegmentRuleRowProps): JSX.Element => {
    const { ruleObject, classes, index, onClickAddSubrule, onClickRemoveSubrule, onClickAddRule, t } = props;
    const { ruleField, ruleCondition, ruleValue } = ruleObject;

    const currentRuleConditionType = ruleField?.ruleConditionType || 'text';
    const pickedCondition = ruleCondition?.value || '=';

    return (
        <div className={classes.rowRoot}>
            <div className={classes.pickersWrapper}>
                {PrintRuleFieldSelect(ruleField, props)}
                <div className={classes.twoLastPickersWrapper}>
                    {PrintRuleConditionSelect(
                        currentRuleConditionType,
                        pickedCondition,
                        handleChangeRuleConditionSelect(props, ruleObject),
                        props
                    )}
                    {PrintRuleValueInput(
                        ruleField,
                        ruleCondition,
                        ruleValue,
                        handleChangeRuleValueInput(props, ruleObject),
                        props
                    )}
                </div>
            </div>
            <div className={classes.ellipsisWrapper}>
                <SimplePopover
                    id={`rule-actions-row-${index}`}
                    customDropdownBody={null}
                    listItemWidth="input-width"
                    maxHeight={400}
                    minWidth={150}
                    onCheckboxClick={noop}
                    onItemClick={(event, pickedItem): void => {
                        switch (pickedItem.value) {
                            case 'delete':
                                onClickRemoveSubrule();
                                break;
                            case 'addSubrule':
                                onClickAddSubrule();
                                break;
                            case 'addRule':
                                onClickAddRule();
                                break;
                            default:
                                break;
                        }
                    }}
                    options={[
                        {
                            label: t('subquery'),
                            value: 'addSubrule',
                        },
                        {
                            label: t('addcondition'),
                            value: 'addRule',
                        },
                        {
                            label: t('delete'),
                            value: 'delete',
                        },
                    ]}
                    type="icon"
                    placement="bottom-end"
                >
                    <span className={classes.dotsIconWrapper}>
                        <DotsIcon />
                    </span>
                </SimplePopover>
            </div>
        </div>
    );
};

export default compose(
    withStyles(styles),
    withTranslation()
)(
    React.memo(
        SegmentRuleRow,
        (prevProps: SegmentRuleRowProps, nextProps: SegmentRuleRowProps): boolean =>
            prevProps.rule === nextProps.rule && prevProps.position === nextProps.position
    )
);
